import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AuthUser } from '@/features/auth/types/auth.types'
import { queryClient } from '@/lib/query-client'

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  user: AuthUser | null
  isAuthenticated: boolean
  mfaToken: string | null
  mfaStep: 'setup' | 'validate' | null
  passwordResetToken: string | null
  passwordResetCode: 'password_reset_required' | 'password_expired' | null
}

interface AuthActions {
  setTokens: (accessToken: string, refreshToken: string | null) => void
  setUser: (user: AuthUser) => void
  clearAuth: () => void
  setMfaPending: (mfaToken: string, step: 'setup' | 'validate') => void
  clearMfa: () => void
  setPasswordResetPending: (
    token: string,
    code: 'password_reset_required' | 'password_expired',
  ) => void
  clearPasswordReset: () => void
}

const initialState: AuthState = {
  accessToken: null,
  refreshToken: null,
  user: null,
  isAuthenticated: false,
  mfaToken: null,
  mfaStep: null,
  passwordResetToken: null,
  passwordResetCode: null,
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      ...initialState,

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken, isAuthenticated: true }),

      setUser: (user) => set({ user }),

      // Limpa o cache do React Query antes de resetar a sessão — sem isso, o próximo usuário
      // a logar no mesmo navegador recebe dados do anterior (queryKeys não incluem o usuário).
      clearAuth: () => {
        queryClient.clear()
        set(initialState)
      },

      setMfaPending: (mfaToken, step) => set({ mfaToken, mfaStep: step }),

      clearMfa: () => set({ mfaToken: null, mfaStep: null }),

      setPasswordResetPending: (token, code) =>
        set({ passwordResetToken: token, passwordResetCode: code }),

      clearPasswordReset: () => set({ passwordResetToken: null, passwordResetCode: null }),
    }),
    {
      name: 'navega-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)
